import {
  AfterContentInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  forwardRef
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { clone } from "lodash";
import { takeUntil } from "rxjs/operators";

import {
  DateService,
  Day,
  Month
} from "@lantern/app/core/utils/date/date.service";
import { Datepicker } from "@lantern/app/models";

import { AbstractDatepickerComponent } from "./abstract-datepicker.component";

@Component({
  selector: "ta-datepicker-inline",
  templateUrl: "./datepicker-inline.template.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerInlineComponent),
      multi: true
    }
  ]
})
export class DatepickerInlineComponent
  extends AbstractDatepickerComponent
  implements OnInit, AfterContentInit, OnDestroy
{
  selectMode: "start" | "end" = "start";
  months: Month[];

  startDateFormatted: string;
  endDateFormatted: string;

  // Starting month for calendar
  @Input()
  set calendarMonth(date: string) {
    this._calendarMonth = date;
    this.months = this.getVisibleMonths();
  }
  get calendarMonth(): string {
    return this._calendarMonth;
  }
  private _calendarMonth: string;

  onDestroyEmitter = new EventEmitter<void>();

  constructor(renderer2: Renderer2, dateService: DateService) {
    super(renderer2, dateService);
  }

  ngOnInit(): void {
    this.months = this.getVisibleMonths();

    this.startDateFormatted = this.dates.startDate;
    this.endDateFormatted = this.dates.endDate;
  }

  ngAfterContentInit(): void {
    super.ngAfterContentInit();
    this.initDateChangeSubscription();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();

    this.onDestroyEmitter.emit();
    this.onDestroyEmitter.complete();
  }

  goToPreviousMonth(): void {
    this.calendarMonth = this.dateService.getPreviousMonth(this.calendarMonth);
  }

  goToNextMonth(): void {
    this.calendarMonth = this.dateService.getNextMonth(this.calendarMonth);
  }

  selectToday(): void {
    this.selectRange(this.dateService.currentDate);
  }

  selectLast7Days(): void {
    this.selectRange(this.dateService.getNDaysFrom(-7));
  }

  selectLast30Days(): void {
    this.selectRange(this.dateService.getNDaysFrom(-30));
  }

  selectWeekToDate(): void {
    this.selectRange(this.dateService.getFirstDayOfWeek());
  }

  selectMonthToDate(): void {
    this.selectRange(this.dateService.getFirstDayOfMonth());
  }

  hoverDates(day: Day): void {
    if (this.selectMode === "end" && day.dateIso! > this.dates.startDate) {
      this.toggleDays({
        startDate: this.dates.startDate,
        endDate: day.dateIso!
      });
    }
  }

  selectDate(day: Day): void {
    if (day.dateIso! < this.dates.startDate || this.single) {
      this.selectMode = "start";
    }

    if (this.selectMode === "start") {
      this.dates = {
        startDate: day.dateIso!,
        endDate: day.dateIso!
      };
      this.selectMode = "end";
    } else {
      this.dates = {
        ...this.dates,
        endDate: day.dateIso!
      };
      this.selectMode = "start";
    }
  }

  selectMonth(month: Month): void {
    this.dates = {
      startDate: month.startDate,
      endDate: month.endDate
    };
  }

  // Handle manual date input
  dateBlur(): void {
    let dates = clone(this.dates);

    if (this.dateService.isValidDate(this.startDateFormatted)) {
      dates.startDate = this.dateService.isoFormatted(this.startDateFormatted);
    } else {
      this.startDateFormatted = this.dates.startDate;
    }

    if (this.dateService.isValidDate(this.endDateFormatted)) {
      dates.endDate = this.dateService.isoFormatted(this.endDateFormatted);
    } else {
      this.endDateFormatted = this.dates.endDate;
    }

    this.dates = dates;
  }

  private getVisibleMonths(): Month[] {
    return this.dateService.getMonthRange(
      -this.monthsBehind,
      this.monthsAhead,
      {
        fromDate: this.calendarMonth,
        toggleDays: this.dates
      }
    );
  }

  private toggleDays(dates: Datepicker = this.dates): void {
    const toggleOption = this.single ? this.dates.startDate : dates;
    this.dateService.toggleDays(this.months, toggleOption);
  }

  private initDateChangeSubscription(): void {
    this.datesChange.pipe(takeUntil(this.onDestroyEmitter)).subscribe(() => {
      this.toggleDays();

      this.startDateFormatted = this.dates.startDate;
      this.endDateFormatted = this.dates.endDate;
    });
  }

  private selectRange(
    startDate: string,
    endDate: string = this.dateService.currentDate
  ): void {
    this.dates = {
      startDate,
      endDate
    };

    this.calendarMonth = endDate;
    this.selectMode = "start";
  }
}
